import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { Wrench } from "@phosphor-icons/react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") nav("/dashboard", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) setErr(r.error);
    else nav("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0C]">
      {/* Left: image */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Machine shop"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0C] via-transparent to-[#0A0A0C]" />
        <div className="relative z-10 h-full p-16 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF4F00] flex items-center justify-center">
              <Wrench weight="bold" size={22} className="text-black" />
            </div>
            <div>
              <div className="font-display font-black text-2xl leading-none">MTM</div>
              <div className="text-[11px] font-mono text-[#FF4F00] tracking-widest">MACHINE SHOP OPS</div>
            </div>
          </div>
          <div>
            <div className="caution-stripes w-40 h-1 mb-6" />
            <h1 className="font-display font-black text-5xl xl:text-6xl leading-none tracking-tight mb-4">
              MACHINES AND TOOLS<br />
              <span className="text-[#FF4F00]">MANAGEMENT</span>
            </h1>
            <p className="text-[#A1A1AA] max-w-md text-base">
              Track every tool. Assign every bin. No lost tools. No downtime. No excuses.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[
                { k: "TOOLS", v: "∞" },
                { k: "USERS", v: "∞" },
                { k: "DOWNTIME", v: "0" },
              ].map((s) => (
                <div key={s.k} className="border border-[#27272A] p-3">
                  <div className="font-mono text-2xl font-bold text-[#FF4F00]">{s.v}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">{s.k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">// AUTHORIZATION</div>
            <h2 className="font-display font-black text-4xl mb-2">SIGN IN</h2>
            <p className="text-[#A1A1AA] text-sm">Enter your credentials to access the shop floor.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1 block">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={AUTH.loginEmail}
                className="w-full bg-[#121214] border border-[#27272A] px-4 py-3 text-white focus:outline-none focus:border-[#FF4F00] transition-colors duration-150"
                placeholder="admin@machineshop.com"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1 block">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={AUTH.loginPassword}
                className="w-full bg-[#121214] border border-[#27272A] px-4 py-3 text-white focus:outline-none focus:border-[#FF4F00] transition-colors duration-150"
                placeholder="••••••••"
              />
            </div>
            {err && (
              <div
                data-testid={AUTH.loginError}
                className="border-l-4 border-[#D32F2F] bg-[#D32F2F]/10 px-4 py-3 text-sm text-[#F5F5F5]"
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid={AUTH.loginSubmit}
              className="brutal-btn w-full bg-[#FF4F00] hover:bg-[#FF6B2B] active:bg-[#E64700] text-black py-3 disabled:opacity-60 transition-colors duration-150"
            >
              {loading ? "Signing in..." : "Sign in →"}
            </button>
          </form>
          <div className="mt-6 text-sm text-[#A1A1AA]">
            No account?{" "}
            <Link data-testid={AUTH.gotoRegister} to="/register" className="text-[#FF4F00] hover:underline font-semibold">
              Request access
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
